import { Certif } from '../CertificatesPage';
import { VoluntaryCertif } from '../VoluntaryCertificatesPage';
import { BadgeTone, PdfBadge, PdfRegistryCard, PdfRegistryField, PdfSummaryCard, PdfSummaryData, PdfTotalCard } from '../types/registryPdf';
import { formatDate } from './dateUtils';
import {
    SummaryCategory,
    buildDynamicsCard,
    buildTotalCard,
    capitalize,
    formatFilterDescription,
    getStatusBadgeTone,
    isMissing,
    normalize,
} from './registryPdfUtils';

type Certificate = Certif | VoluntaryCertif;

const COMMENT_CATEGORIES: { label: string; value: string; color: string; tone: BadgeTone }[] = [
    { label: 'Есть нарушения', value: 'есть нарушения', color: 'rgb(237, 93, 98)', tone: 'negative' },
    { label: 'Нарушений нет', value: 'нарушений нет', color: 'rgb(126, 178, 70)', tone: 'positive' },
    { label: 'Обратить внимание', value: 'обратить внимание', color: 'rgb(255, 166, 0)', tone: 'warning' },
];

const STATUS_CATEGORIES = [
    { label: 'Действует', values: ['действует', 'действующий'], color: 'rgb(126, 178, 70)' },
    { label: 'Прекращен', values: ['прекращен', 'прекращенный'], color: 'rgb(237, 93, 98)' },
    { label: 'Архивный', values: ['архивный', 'архивирован'], color: 'rgb(247, 0, 150)' },
    { label: 'Недействителен', values: ['недействителен', 'недействительный'], color: '#1f2630' },
    { label: 'Приостановлен', values: ['приостановлен', 'приостановленный'], color: 'rgb(255, 166, 0)' },
];

const OTHER_STATUS_COLORS = ['#6f42c1', '#17a2b8', '#6c757d', '#996633'];
const EMPTY_VALUE_COLOR = '#c3c9d1';

const FILTER_LABELS: { [field: string]: string } = {
    cert_reg_number: 'номер сертификата',
    os_name: 'орган по сертификации',
    os_number: 'номер органа по сертификации',
    applicant_shortname: 'заявитель',
    applicant_inn: 'ИНН заявителя',
    applicant_region: 'регион заявителя',
    manufacture_fullname: 'изготовитель',
    manufacture_inn: 'ИНН изготовителя',
    region_manufacturer: 'регион изготовителя',
    cert_reg_data: 'дата регистрации',
};


const getCertificateYear = (cert: Certificate): string => {
    return cert.cert_reg_data ? String(cert.cert_reg_data).substring(0, 4) : '';
};


// "Без комментария" нужен, чтобы итог на диаграмме сходился с количеством сертификатов
const getCommentCategories = (certificates: Certificate[]): SummaryCategory<Certificate>[] => {
    const knownComments = COMMENT_CATEGORIES.map(category => category.value);
    const categories: SummaryCategory<Certificate>[] = COMMENT_CATEGORIES.map(category => ({
        label: category.label,
        color: category.color,
        matches: (cert: Certificate) => normalize(cert.comment_apts) === category.value,
    }));
    const hasNoComment = (cert: Certificate) => !knownComments.includes(normalize(cert.comment_apts));
    if (certificates.some(hasNoComment)) {
        categories.push({ label: 'Без комментария', color: EMPTY_VALUE_COLOR, matches: hasNoComment });
    }
    return categories;
};


// Статусы ФСА плюс другие статусы, которые встретились в данных (например, "Отменен" у добровольных)
const getStatusCategories = (certificates: Certificate[], showEmptyStatuses: boolean): SummaryCategory<Certificate>[] => {
    const categories: SummaryCategory<Certificate>[] = [];
    STATUS_CATEGORIES.forEach(category => {
        const matches = (cert: Certificate) => category.values.includes(normalize(cert.cert_status));
        if (showEmptyStatuses || certificates.some(matches)) {
            categories.push({ label: category.label, color: category.color, matches });
        }
    });

    const knownStatuses: string[] = [];
    STATUS_CATEGORIES.forEach(category => knownStatuses.push(...category.values));
    const otherStatuses: string[] = [];
    certificates.forEach(cert => {
        const status = normalize(cert.cert_status);
        if (status && !knownStatuses.includes(status) && !otherStatuses.includes(status)) otherStatuses.push(status);
    });
    otherStatuses.forEach((status, index) => {
        categories.push({
            label: capitalize(status),
            color: OTHER_STATUS_COLORS[index % OTHER_STATUS_COLORS.length],
            matches: (cert: Certificate) => normalize(cert.cert_status) === status,
        });
    });

    if (certificates.some(cert => !normalize(cert.cert_status))) {
        categories.push({ label: 'Не указан', color: EMPTY_VALUE_COLOR, matches: (cert: Certificate) => !normalize(cert.cert_status) });
    }
    return categories;
};


export const buildCommentCard = (title: string, certificates: Certificate[], showDynamics: boolean): PdfTotalCard => {
    return buildTotalCard(title, certificates, getCommentCategories(certificates), getCertificateYear, showDynamics);
};


export const describeCertificateFilters = (
    aptsComments: string[],
    certStatuses: string[],
    years: string[],
    filters: { [field: string]: string[] },
): string => {
    const conditions = [
        { label: 'комментарий АПТС', values: aptsComments },
        { label: 'статус', values: certStatuses },
        { label: 'год', values: [...years].sort(), quoted: false },
    ];
    Object.keys(filters).forEach(field => {
        conditions.push({ label: FILTER_LABELS[field] || field, values: filters[field] });
    });
    return formatFilterDescription(conditions);
};


export interface CertificatesSummaryOptions {
    title: string;
    statusTitle: string;
    filterDescription: string;
    // У обязательных нулевые статусы ФСА показываются, как в макете
    showEmptyStatuses: boolean;
    showCommentDynamics: boolean;
    showStatusDynamics: boolean;
    showCertificationBodies: boolean;
    showLaboratories: boolean;
    showApplicants: boolean;
    showManufacturers: boolean;
}


export const buildCertificatesSummary = (certificates: Certificate[], options: CertificatesSummaryOptions): PdfSummaryData => {
    const cards: PdfSummaryCard[] = [
        buildCommentCard('Комментарий АПТС', certificates, options.showCommentDynamics),
        buildTotalCard(options.statusTitle, certificates, getStatusCategories(certificates, options.showEmptyStatuses), getCertificateYear, options.showStatusDynamics),
    ];

    if (options.showCertificationBodies) {
        cards.push(buildDynamicsCard('Органы по сертификации — динамика', 'Прочие органы', certificates, getCertificateYear, cert => {
            if (!cert.os_name) return [];
            return [{ key: cert.os_number || cert.os_name, name: cert.os_name }];
        }));
    }

    if (options.showLaboratories) {
        cards.push(buildDynamicsCard('Лаборатории — динамика', 'Прочие лаборатории', certificates, getCertificateYear, cert => {
            const labNames = [cert.lan_name_1, cert.lan_name_2, cert.lan_name_3].map(name => (name || '').trim());
            return labNames.filter(name => !isMissing(name)).map(name => ({ key: name, name }));
        }));
    }

    if (options.showApplicants) {
        cards.push(buildDynamicsCard('Заявители — динамика', 'Прочие заявители', certificates, getCertificateYear, cert => {
            const applicantName = getApplicantName(cert);
            const applicantKey = (cert.applicant_inn || '').trim() || applicantName;
            if (!applicantKey) return [];
            return [{ key: applicantKey, name: applicantName || applicantKey }];
        }));
    }

    if (options.showManufacturers) {
        cards.push(buildDynamicsCard('Изготовители — динамика', 'Прочие изготовители', certificates, getCertificateYear, cert => {
            const manufacturerName = (cert.manufacture_fullname || '').trim();
            const manufacturerKey = (cert.manufacture_inn || '').trim() || manufacturerName;
            if (!manufacturerKey || isMissing(manufacturerName)) return [];
            return [{ key: manufacturerKey, name: manufacturerName }];
        }));
    }

    return {
        title: options.title,
        subtitle: `Фильтр: ${options.filterDescription}`,
        counters: [{ label: 'Общее количество сертификатов', value: certificates.length }],
        cards,
    };
};


const getApplicantName = (cert: Certificate): string => {
    const shortName = (cert.applicant_shortname || '').trim();
    if (!isMissing(shortName)) return shortName;
    const fullName = (cert.applicant_fullname || '').trim();
    return isMissing(fullName) ? '' : fullName;
};


const getStatusBadge = (cert: Certificate): PdfBadge | null => {
    if (!cert.cert_status) return null;
    const category = STATUS_CATEGORIES.find(statusCategory => statusCategory.values.includes(normalize(cert.cert_status)));
    const statusLabel = category ? category.label : capitalize(cert.cert_status.trim());
    return { text: statusLabel, tone: getStatusBadgeTone(statusLabel) };
};


const getValidityField = (cert: Certificate): PdfRegistryField => {
    const dates = [formatDate(cert.cert_reg_data), formatDate(cert.cert_finish_data)].filter(Boolean);
    return { label: 'Срок действия', entries: [{ value: dates.join(' – ') }] };
};


const getCommentField = (cert: Certificate): PdfRegistryField => {
    if (!cert.comment_apts) return { label: 'Комментарий АПТС', entries: [] };
    const category = COMMENT_CATEGORIES.find(commentCategory => commentCategory.value === normalize(cert.comment_apts));
    return {
        label: 'Комментарий АПТС',
        badge: { text: capitalize(cert.comment_apts.trim()), tone: category ? category.tone : 'neutral' },
        entries: [],
    };
};


const getLaboratoriesField = (cert: Certificate): PdfRegistryField => {
    const labs = [
        { value: cert.lan_name_1, hint: cert.lab_number_1 },
        { value: cert.lan_name_2, hint: cert.lab_number_2 },
        { value: cert.lan_name_3, hint: cert.lab_number_3 },
    ].filter(lab => !isMissing(lab.value));
    return { label: labs.length > 1 ? 'Лаборатории' : 'Лаборатория', entries: labs };
};


const joinHints = (hints: (string | null | undefined)[]): string => hints.filter(Boolean).join(' · ');


export const buildCertificateRegistryCard = (cert: Certificate): PdfRegistryCard => ({
    number: cert.cert_reg_number,
    status: getStatusBadge(cert),
    fields: [
        getValidityField(cert),
        { label: 'Схема', entries: [{ value: cert.cert_schema }] },
        getCommentField(cert),
        {
            label: 'Заявитель',
            entries: [{
                value: getApplicantName(cert),
                hint: joinHints([cert.applicant_inn && `ИНН ${cert.applicant_inn}`, cert.applicant_region]),
            }],
        },
        {
            label: 'Изготовитель',
            entries: [{
                value: isMissing(cert.manufacture_fullname) ? '' : cert.manufacture_fullname,
                hint: joinHints([cert.manufacture_inn && `ИНН ${cert.manufacture_inn}`, cert.region_manufacturer]),
            }],
        },
        { label: 'Орган по сертификации', entries: [{ value: cert.os_name, hint: cert.os_number }] },
        getLaboratoriesField(cert),
    ],
    sections: [
        { title: 'Продукция', text: cert.product_fullname },
        { title: 'Проверена на соответствие', text: cert.regulatory_documentation },
        { title: 'Изготовлено в соответствии', text: cert.technical_docs },
    ],
});


// У добровольных нет заявителя и техдокументации, а в cert_schema система сертификации
export const buildVoluntaryCertificateRegistryCard = (cert: Certificate): PdfRegistryCard => ({
    number: cert.cert_reg_number,
    status: getStatusBadge(cert),
    fields: [
        getValidityField(cert),
        { label: 'Система сертификации', entries: [{ value: cert.cert_schema }] },
        getCommentField(cert),
        {
            label: 'Изготовитель',
            entries: [{
                value: isMissing(cert.manufacture_fullname) ? '' : cert.manufacture_fullname,
                hint: cert.manufacture_inn ? `ИНН/УНП ${cert.manufacture_inn}` : '',
            }],
        },
        { label: 'Орган по сертификации', entries: [{ value: cert.os_name, hint: cert.os_number }] },
        getLaboratoriesField(cert),
    ],
    sections: [
        { title: 'Продукция', text: cert.product_fullname },
        { title: 'Проверена на соответствие', text: cert.regulatory_documentation },
    ],
});
